import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
//import { ResponsiveLine } from '@nivo/line';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Doughnut } from 'react-chartjs-2';
import { jwtDecode } from 'jwt-decode';
import 'chart.js/auto';
import '../css/Dashboard.css';  // 스타일 파일을 별도로 분리하여 import
import NavigationBar from '../components/NavigationBar'; // NavigationBar 컴포넌트 import
import { API_BASE_URL } from '../config/config';
import {
    PATIENTS_PAGE_SIZE,
    toPatientRow,
    fetchPatientsPage,
    fetchPatientRiskStats
} from '../utils/patientsApi';
import RiskDot, { RiskDots } from '../components/RiskDot';
import { calculateAge } from '../utils/age';
import { formatGender } from '../utils/gender';
import getRiskLevel from '../utils/dashboardRiskLevel';
import { Search, Circle, ChevronLeft, ChevronRight, X } from 'lucide-react';

// calculateTotalRiskScore 는 여기서 제거했다. 전체 사용자를 받아 위험도순으로
// 정렬하던 코드에서만 쓰였는데, 그 정렬은 집계용 사본에만 적용되고 화면 목록에는
// 반영되지 않는 죽은 계산이었다. 서버 페이지네이션으로 옮기며 함께 사라졌다.

// 사용자별로 가장 최근 측정값을 구하는 함수
const getLatestMeasureValue = (records, type) => {
    if (!records) return null;
    if (type === 'systolic' && records.bloodPressure && records.bloodPressure.systolic) {
        return records.bloodPressure.systolic.value;
    }
    if (type === 'diastolic' && records.bloodPressure && records.bloodPressure.diastolic) {
        return records.bloodPressure.diastolic.value;
    }
    if (type === 'bloodGlucose' && records.bloodGlucose) {
        return records.bloodGlucose.value;
    }
    if (type === 'bmi' && records.bmi) {
        return records.bmi.value;
    }
    return null;
};

// 사용자별 가장 최근 측정일시를 구하는 함수
const getLatestUserMeasureDate = (user) => {
    const dates = [];
    if (user.bloodPressure && user.bloodPressure.systolic && user.bloodPressure.systolic.measure_date) {
        dates.push(new Date(user.bloodPressure.systolic.measure_date));
    }
    if (user.bloodPressure && user.bloodPressure.diastolic && user.bloodPressure.diastolic.measure_date) {
        dates.push(new Date(user.bloodPressure.diastolic.measure_date));
    }
    if (user.bloodGlucose && user.bloodGlucose.measure_date) {
        dates.push(new Date(user.bloodGlucose.measure_date));
    }
    if (user.bmi && user.bmi.measure_date) {
        dates.push(new Date(user.bmi.measure_date));
    }
    if (dates.length === 0) return null;
    return new Date(Math.max.apply(null, dates));
};

// 목록 상태를 세션에 담는 열쇠. 화면이 하나뿐이라 고정 문자열이면 충분하다.
const LIST_STATE_KEY = 'dashboardListState';
const FILTER_TYPES = ['all', 'region', 'hospital'];

const readListState = () => {
    // sessionStorage 는 사생활 보호 모드·차단 설정에서 던질 수 있다. 실패하면 기본값이다.
    try {
        const saved = JSON.parse(sessionStorage.getItem(LIST_STATE_KEY) || '{}');
        return {
            page: Number.isSafeInteger(saved.page) && saved.page > 0 ? saved.page : 1,
            searchTerm: typeof saved.searchTerm === 'string' ? saved.searchTerm : '',
            // 필터 종류는 아래 fetchData 가 쓰는 값들이다. 모르는 값이면 전체로 본다.
            filterType: FILTER_TYPES.includes(saved.filterType) ? saved.filterType : 'all'
        };
    } catch (error) {
        return { page: 1, searchTerm: '', filterType: 'all' };
    }
};

const writeListState = (state) => {
    try {
        sessionStorage.setItem(LIST_STATE_KEY, JSON.stringify(state));
    } catch (error) {
        // 저장하지 못해도 화면은 그대로 돈다. 다음에 돌아올 때 1쪽일 뿐이다.
    }
};

const StatsAndTable = () => {
    const location = useLocation();
    const [data, setData] = useState({
        stats: {
            totalRisk: 0,
            caution: { hypertension: 0, bloodSugar: 0 },
            risk: { hypertension: 0, bloodSugar: 0 },
            severeRisk: { hypertension: 0, bloodSugar: 0 },
        },
        users: [],
        currentPage: 1,
        totalPages: 1,
    });

    // 목록 상태(쪽·검색어·필터)를 세션에 남긴다.
    //
    // 브레드크럼의 '대시보드' 버튼은 라우터 state 로 상태를 돌려주지만,
    // **브라우저 뒤로가기는 그 경로를 타지 않는다.** 대시보드 기록에는 state 가 없어
    // (게다가 복원 직후 replaceState 로 지운다) 1쪽·검색어 없음으로 되돌아갔다.
    // 3쪽에서 대상자를 열어 본 담당자가 매번 1쪽부터 다시 찾아야 했다(2026-09-17 보고).
    //
    // 세션에만 둔다 — 브라우저를 닫으면 사라진다. localStorage 에 두면 다음 날 출근해
    // 열었을 때도 지난번 검색어가 남아 "왜 목록이 이상하지"가 된다.
    const restored = readListState();
    const [page, setPage] = useState(restored.page);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterType, setFilterType] = useState(restored.filterType);
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState(restored.searchTerm);
    const [inputValue, setInputValue] = useState(restored.searchTerm);
    const searchInputRef = useRef(null);
    const [isComposing, setIsComposing] = useState(false);

    // 중점관리 연동 필터 상태
    const [intensiveFilter, setIntensiveFilter] = useState(null);
    const [groupUserIds, setGroupUserIds] = useState([]);

    useEffect(() => {
        const handleFilterChange = (event) => {
            setFilterType(event.detail.type);
        };
        window.addEventListener('filterChange', handleFilterChange);
        return () => window.removeEventListener('filterChange', handleFilterChange);
    }, []);

    // 외부 프로그램 SSO 진입(role:'external')일 때만 목록을 그 사용자 한 명으로 좁힌다.
    // ⚠️ localStorage 마커는 세션을 넘어 남는다. 반드시 "현재 토큰이 external 세션인지"
    //    직접 확인한 뒤에만 적용한다. 아니면(=일반 관리자 로그인) 남은 마커를 청소해
    //    다른 계정 세션에 필터가 누수되지 않게 한다.
    useEffect(() => {
        const decodeRole = (t) => {
            try {
                const payload = t.split('.')[1];
                return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))).role;
            } catch (e) { return null; }
        };
        const token = localStorage.getItem('token');
        const isExternal = !!token && decodeRole(token) === 'external';
        const onlyId = localStorage.getItem('external_only_app_user_id');

        if (isExternal && onlyId && /^\d+$/.test(onlyId)) {
            const label = localStorage.getItem('external_only_label') || '외부 연동';
            setIntensiveFilter(label);
            setGroupUserIds([Number(onlyId)]);
            setPage(1);
        } else {
            localStorage.removeItem('external_only_app_user_id');
            localStorage.removeItem('external_only_label');
        }
    }, []);

    // 쪽·검색어·필터가 바뀔 때마다 세션에 남긴다. 다음에 이 화면으로 돌아오면
    // (뒤로가기 포함) 그 자리에서 이어 본다.
    useEffect(() => {
        writeListState({ page, searchTerm, filterType });
    }, [page, searchTerm, filterType]);

    // Dashboard 상태 복원 및 중점관리 연동
    useEffect(() => {
        const state = location.state;
        if (state) {
            if (state.intensiveFilter && state.groupUserIds) {
                console.log('[Dashboard] 중점관리 그룹 연동:', state.intensiveFilter);
                setIntensiveFilter(state.intensiveFilter);
                setGroupUserIds(state.groupUserIds);
                setPage(1); // 페이지 초기화
            } else if (state.restoreState) {
                console.log('[Dashboard] 상태 복원:', state.restoreState);
                setPage(state.restoreState.currentPage || 1);
                setSearchTerm(state.restoreState.searchTerm || '');
                setFilterType(state.restoreState.filterType || 'all');
                setInputValue(state.restoreState.searchTerm || '');
            }
            // 상태 복원 후 location.state를 클리어하여 중복 실행 방지
            window.history.replaceState({}, document.title);
        }
    }, [location.state]);

    useEffect(() => {
        const userRole = localStorage.getItem('user_role') || (JSON.parse(localStorage.getItem('adminInfo') || '{}').role);
        if (userRole === 'guardian') return; // 보호자면 검증 스킵
        const token = localStorage.getItem('token');

        if (!token) {
            alert('로그인이 필요합니다.');
            navigate('/');
            return;
        }

        try {
            const decodedToken = jwtDecode(token);
            const currentTime = Date.now() / 1000;

            if (decodedToken.exp < currentTime) {
                alert('세션이 만료되었습니다. 다시 로그인해주세요.');
                localStorage.removeItem('token');
                navigate('/');
                return;
            }
        } catch (error) {
            console.error('토큰 검증 실패:', error);
            alert('인증에 실패했습니다. 다시 로그인해주세요.');
            localStorage.removeItem('token');
            navigate('/');
            return;
        }
    }, [navigate]);

    useEffect(() => {
        const fetchGuardianData = async () => {
            // 예시: 보호자용 데이터 API 호출
            try {
                const token = localStorage.getItem('token');
                // 실제 보호자용 API 엔드포인트로 교체 필요
                const response = await axios.get(`${API_BASE_URL}/api/guardian-dashboard-data`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (response.data.success) {
                    setData(response.data.data); // 보호자용 데이터 구조에 맞게 가공 필요
                } else {
                    setError('보호자 데이터를 불러오는 데 실패했습니다.');
                }
            } catch (error) {
                setError('보호자 데이터를 불러오는 중 오류가 발생했습니다.');
            } finally {
                setLoading(false);
            }
        };

        const userRole = localStorage.getItem('user_role') || (JSON.parse(localStorage.getItem('adminInfo') || '{}').role);
        if (userRole === 'guardian') {
            setLoading(true);
            setError(null);
            fetchGuardianData();
            return;
        }
        // 기존 fetchData
        const fetchData = async () => {
            setLoading(true);
            setError(null);
            try {
                const token = localStorage.getItem('token');

                const user = JSON.parse(localStorage.getItem('user'));
                const hospitalId = user?.hospital_id || localStorage.getItem('hospital_id');
                const regionCode = user?.region_code || localStorage.getItem('region_code');
                const districtCode = user?.district_code || localStorage.getItem('district_code');
                const isSuperAdmin = user?.is_super_admin === 'true' || localStorage.getItem('is_super_admin') === 'true';
                const groupIds = localStorage.getItem('group_ids');

                // user_group_ids 처리 추가
                let userGroupIds = user?.user_group_ids || localStorage.getItem('user_group_ids');
                if (typeof userGroupIds === 'string' && userGroupIds.startsWith('[')) {
                    try {
                        userGroupIds = JSON.parse(userGroupIds);
                    } catch (e) { }
                }
                let userGroupIdsParam = Array.isArray(userGroupIds) ? userGroupIds.join(',') : userGroupIds;

                // 필터 타입에 따라 파라미터 다르게 설정
                let params = {};
                if (isSuperAdmin) {
                    // 슈퍼 관리자는 is_super_admin 파라미터만 전송
                    params = {
                        is_super_admin: 'true'
                    };
                } else if (userGroupIdsParam) {
                    params = {
                        user_group_ids: userGroupIdsParam
                    };
                    console.log('Using user_group_ids for filtering:', params.user_group_ids);
                } else {
                    // 일반 관리자는 기존 필터 적용
                    if (filterType === 'all') {
                        params = {
                            hospital_id: hospitalId,
                            region_code: regionCode,
                            district_code: districtCode
                        };
                    } else if (filterType === 'region') {
                        params = {
                            region_code: regionCode,
                            district_code: districtCode
                        };
                    } else if (filterType === 'hospital') {
                        params = {
                            hospital_id: hospitalId
                        };
                    }
                }

                console.log('API 요청 파라미터:', params);

                // 목록은 서버가 한 페이지만 잘라서 준다. 검색어와 중점관리 필터도
                // 서버에서 적용해야 검색이 현재 페이지 밖까지 미친다.
                // 위험 현황은 SQL 집계라 목록과 별개로 받는다.
                // 검색어를 넘기지 않는다 — 예전에도 상단 카드는 필터링 전 전체 기준이었다.
                const [list, stats] = await Promise.all([
                    fetchPatientsPage({
                        token,
                        scopeParams: params,
                        page,
                        size: PATIENTS_PAGE_SIZE,
                        search: searchTerm || undefined,
                        appUserIds: intensiveFilter ? (groupUserIds || []).join(',') : undefined
                    }),
                    fetchPatientRiskStats({ token, scopeParams: params })
                ]);

                const users = (list.items || []).map(item => toPatientRow(item, { getRiskLevel }));
                const totalPages = list.totalPages || 0;

                setData({
                    stats,
                    users,
                    currentPage: list.page,
                    totalPages: totalPages || 1
                });

                // 마지막 페이지를 보던 중 건수가 줄면 범위를 벗어난다. 원래 있던 보정.
                if (totalPages > 0 && page > totalPages) {
                    setPage(totalPages);
                }
            } catch (error) {
                console.error('Fetch Error:', error);
                if (error.response) {
                    console.error('Error Response:', error.response);
                }

                if (error.response && error.response.status === 401) {
                    alert('인증이 만료되었습니다. 다시 로그인해주세요.');
                    localStorage.removeItem('token');
                    navigate('/');
                } else {
                    console.error('Error fetching data:', error);
                    setError('데이터를 불러오는 중 오류가 발생했습니다.');
                }
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [page, navigate, filterType, searchTerm, intensiveFilter, groupUserIds]);


    // 검색 실행 함수
    const handleSearch = (e) => {
        if (e) e.preventDefault();
        setSearchTerm(inputValue);
        if (page !== 1) setPage(1);
    };

    const clearIntensiveFilter = () => {
        setIntensiveFilter(null);
        setGroupUserIds([]);
        setPage(1);
    };

    // 검색어가 바뀔 때만 input에 포커스 유지
    useEffect(() => {
        if (searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, [searchTerm]);

    // searchTerm이 바뀌면 inputValue도 동기화
    useEffect(() => {
        setInputValue(searchTerm);
    }, [searchTerm]);

    const dateFormatter = (date) => {
        if (!date) return '-';
        const d = new Date(date);
        // KST(UTC+9)로 변환
        const kst = new Date(d.getTime() + 9 * 60 * 60 * 1000);
        return kst.toISOString().split('T')[0];
    };

    // 대상자 칸의 보조 줄. 나이 · 성별 · 기관 순서다.
    // 나이는 만 나이다(utils/age.js). 생년월일이 없거나 '-' 인 대상자는 그 조각만 빠진다.
    const personMeta = (user) => {
        const age = calculateAge(user.birthdate);
        const institution = user.group_name || user.management_group_name || user.management_group;
        return [
            age != null ? `${age}세` : null,
            formatGender(user.gender) || null,
            institution || null
        ].filter(Boolean).join(' · ');
    };

    // "최근 측정"을 사람이 읽는 말로 바꾼다.
    // 30일을 넘기면 "N일 전"이 오히려 가늠이 안 되므로 날짜를 그대로 보여준다.
    // 날짜 계산은 dateFormatter 와 같은 KST 기준으로 맞춘다 — 둘이 다르면 자정 근처에서
    // "오늘"이라고 적힌 줄과 어제 날짜가 같이 나온다.
    const measuredAgoText = (value) => {
        if (!value) return '—';
        const day = dateFormatter(value);
        if (day === '-') return '—';
        const today = dateFormatter(new Date());
        const diff = (Date.parse(`${today}T00:00:00Z`) - Date.parse(`${day}T00:00:00Z`)) / 86400000;
        if (!Number.isFinite(diff)) return '—';
        if (diff <= 0) return '오늘';
        if (diff === 1) return '어제';
        if (diff <= 30) return `${diff}일 전`;
        return day;
    };

    const handleServiceCheckboxChange = async (user) => {
        const token = localStorage.getItem('token');
        try {
            const response = await axios.patch(
                `${API_BASE_URL}/api/health-data/${user.app_user_id}/management-service`,
                { management_service_yn: !user.management_service_yn },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (response.data.success) {
                // 서버 페이지네이션으로 바뀌면서 allUsers 사본이 없어졌다.
                // 화면에 있는 현재 페이지만 갱신한다.
                setData(prev => ({
                    ...prev,
                    users: prev.users.map(u =>
                        u.app_user_id === user.app_user_id
                            ? { ...u, management_service_yn: !u.management_service_yn }
                            : u
                    )
                }));
            } else {
                alert('업데이트에 실패했습니다.');
            }
        } catch (error) {
            alert('업데이트 중 오류가 발생했습니다.');
        }
    };


    return (
        <div className="sds-page dashboard-content">
            {loading && (
                <div className="sds-notice">데이터를 불러오는 중...</div>
            )}
            {!loading && error && (
                <div className="sds-notice sds-notice--error">{error}</div>
            )}
            {/* 기존 레이아웃은 항상 표시 */}
            <>
                {/* 위험 현황 카드는 화면에서 숨긴다 (2026-08-13).
                    지우지 않고 주석으로 남기는 이유: 집계 자체는 서버에서 정상 동작하고,
                    다시 켜 달라는 요청이 오면 이 블록의 주석만 걷으면 된다.
                    /api/patients/stats 호출도 그대로 둔다 — 가벼운 집계 한 줄이라
                    빼서 얻는 것보다 나중에 되살릴 때 빠뜨릴 위험이 크다.
                    마크업은 2026-09-10 sds-stat-card 규격으로 바꿔 두었다(design.md 6.3). */}
                {false && (<>
                <h2 className="sds-section-title">위험 현황 (최근 7일)</h2>
                <div className="sds-stat-grid">
                    <div className="sds-stat-card sds-stat-card--danger">
                        <div className="sds-stat-card__value">{data.stats.totalRisk}<span className="sds-stat-card__unit">명</span></div>
                        <div className="sds-stat-card__label">전체 위험</div>
                    </div>
                    <div className="sds-stat-card sds-stat-card--danger">
                        <div className="sds-stat-card__value">
                            {data.stats.severeRisk.hypertension}<span className="sds-stat-card__unit">/</span>{data.stats.severeRisk.bloodSugar}
                        </div>
                        <div className="sds-stat-card__label">심각 위험 (혈압/혈당)</div>
                    </div>
                    <div className="sds-stat-card sds-stat-card--danger">
                        <div className="sds-stat-card__value">
                            {data.stats.risk.hypertension}<span className="sds-stat-card__unit">/</span>{data.stats.risk.bloodSugar}
                        </div>
                        <div className="sds-stat-card__label">위험 (혈압/혈당)</div>
                    </div>
                    <div className="sds-stat-card sds-stat-card--caution">
                        <div className="sds-stat-card__value">
                            {data.stats.caution.hypertension}<span className="sds-stat-card__unit">/</span>{data.stats.caution.bloodSugar}
                        </div>
                        <div className="sds-stat-card__label">주의 (혈압/혈당)</div>
                    </div>
                </div>
                </>)}

                <div className="sds-page-header">
                    <h1 className="sds-page-header__title">
                        사용자 목록
                        {intensiveFilter && (
                            <button
                                onClick={clearIntensiveFilter}
                                className="sds-chip sds-chip--danger sds-chip--active"
                                title="필터 해제"
                            >
                                {intensiveFilter}
                                <X size={16} aria-hidden="true" />
                            </button>
                        )}
                    </h1>
                </div>

                <div className="sds-toolbar">
                    <span className="sds-toolbar__spacer" />
                    <form onSubmit={handleSearch} className="sds-toolbar__form">
                        <div className="sds-input-group">
                            <Search size={20} aria-hidden="true" />
                            <input
                                ref={searchInputRef}
                                type="text"
                                placeholder="이름 또는 관리그룹으로 검색"
                                value={inputValue}
                                onChange={e => setInputValue(e.target.value)}
                                className="sds-input dashboard-search-input"
                            />
                        </div>
                        {/* 이 폼의 제출 버튼이다 — type 을 적지 않아 form 안 버튼의 기본값 submit 으로 동작한다. */}
                        <button className="sds-btn sds-btn--secondary">검색</button>
                    </form>
                </div>

                <div className="sds-legend dashboard-legend" aria-label="위험도 색 안내">
                    <span><RiskDot level="위험 (고혈압)" decorative />위험</span>
                    <span><RiskDot level="경고 (고혈압 주의)" decorative />경고</span>
                    <span><RiskDot level="정상" decorative />정상</span>
                    <span><RiskDot level="위험 (저혈압)" decorative />위험 (낮음)</span>
                    <span><RiskDot level="기록 없음" decorative />기록 없음</span>
                </div>

                <div className="sds-table-wrap">
                <table className="sds-table dashboard-table">
                    <thead>
                        <tr>
                            {/* 번호는 쪽을 넘겨도 이어진다(2쪽 첫 줄이 11번). 담당자가
                                "몇 번째 사람"으로 서로 부르던 옛 표의 쓰임을 되살린 것이다. */}
                            <th className="sds-table__center dashboard-page__no">번호</th>
                            <th>대상자</th>
                            <th className="sds-table__center">혈압</th>
                            <th className="sds-table__center">공복혈당</th>
                            <th className="sds-table__center">식후혈당</th>
                            <th className="sds-table__center">BMI</th>
                            <th>최근 측정</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.users.length > 0 ? (
                            data.users.map((user, index) => (
                                <tr key={index}>
                                    <td className="sds-table__center dashboard-page__no">
                                        {((data.currentPage || page) - 1) * PATIENTS_PAGE_SIZE + index + 1}
                                    </td>
                                    {/* 대상자 한 칸에 신원을 모은다. 옛 표는 번호·이름·관리그룹·
                                        생년월일·성별을 다섯 칸으로 벌려 놓아 가로가 길었다.
                                        정확한 생년월일은 상세 화면 헤더가 들고 있다. */}
                                    <td>
                                        <span className="sds-person">
                                            {/* 반짝임은 시선을 잡아 두는 장식이다. 중립색 하나로
                                                고정한다 — 신호등 색을 쓰면 상태 신호로 읽힌다. */}
                                            <span className="sds-person__avatar" aria-hidden="true">
                                                {(user.name || '?').trim().charAt(0)}
                                            </span>
                                            <span className="sds-person__text">
                                                <span className="sds-person__name">
                                                    <button
                                                        onClick={() => navigate(`/DashboardDetail/${user.app_user_id || user.user_id}`, {
                                                            state: {
                                                                dashboardState: {
                                                                    currentPage: page,
                                                                    searchTerm: searchTerm,
                                                                    filterType: filterType,
                                                                    totalPages: data.totalPages
                                                                }
                                                            }
                                                        })}
                                                        className="sds-link"
                                                    >
                                                        {user.name}
                                                    </button>
                                                    {/* 손가락에 낀 반지를 위에서 본 모양 —
                                                        lucide 에 반지 아이콘이 따로 없어 테두리만
                                                        있는 원을 쓴다. 수치 칸의 신호등은 색으로
                                                        채운 작은 원이라 서로 헷갈리지 않는다. */}
                                                    {user.has_smart_ring && (
                                                        <Circle
                                                            size={16}
                                                            strokeWidth={3}
                                                            className="sds-person__icon sds-person__icon--ring"
                                                            aria-label="스마트링 착용 중"
                                                            role="img"
                                                        />
                                                    )}
                                                </span>
                                                <span className="sds-person__meta">{personMeta(user)}</span>
                                            </span>
                                        </span>
                                    </td>
                                    <td className="sds-table__center">
                                        {user.bloodPressure && (user.bloodPressure.systolic || user.bloodPressure.diastolic) ? (
                                            <RiskDots items={[
                                                user.bloodPressure.systolic && {
                                                    level: user.bloodPressure.systolic.risk.level,
                                                    title: `수축기: ${user.bloodPressure.systolic.value} (${user.bloodPressure.systolic.risk.level})`
                                                },
                                                user.bloodPressure.diastolic && {
                                                    level: user.bloodPressure.diastolic.risk.level,
                                                    title: `이완기: ${user.bloodPressure.diastolic.value} (${user.bloodPressure.diastolic.risk.level})`
                                                }
                                            ].filter(Boolean)} />
                                        ) : <span className="dashboard-cell-empty">—</span>}
                                    </td>
                                    <td className="sds-table__center">
                                        {user.bloodGlucose ? (
                                            <RiskDot
                                                level={user.bloodGlucose.risk.level}
                                                title={`공복혈당: ${user.bloodGlucose.value} (${user.bloodGlucose.risk.level})`}
                                            />
                                        ) : <span className="dashboard-cell-empty">—</span>}
                                    </td>
                                    <td className="sds-table__center">
                                        {user.bloodGlucoseMeal ? (
                                            <RiskDot
                                                level={user.bloodGlucoseMeal.risk.level}
                                                title={`식후혈당: ${user.bloodGlucoseMeal.value} (${user.bloodGlucoseMeal.risk.level})`}
                                            />
                                        ) : <span className="dashboard-cell-empty">—</span>}
                                    </td>
                                    <td className="sds-table__center">
                                        {user.bmi ? (
                                            <RiskDot
                                                level={user.bmi.risk.level}
                                                title={`BMI: ${user.bmi.value} (${user.bmi.risk.level})`}
                                            />
                                        ) : <span className="dashboard-cell-empty">—</span>}
                                    </td>
                                    <td className="sds-table__muted">{measuredAgoText(getLatestUserMeasureDate(user))}</td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="7" className="sds-empty">
                                    데이터가 없습니다.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
                </div>
                <div className="sds-pagination">
                    <button
                        className="sds-pagination__btn"
                        onClick={() => setPage(Math.max(page - 1, 1))}
                        disabled={page <= 1}
                        aria-label="이전 페이지"
                    >
                        <ChevronLeft size={16} aria-hidden="true" />
                    </button>
                    {(() => {
                        const totalPages = data.totalPages;
                        const currentPage = page;
                        const maxVisiblePages = 5; // 최대 표시할 페이지 번호 수

                        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
                        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

                        // endPage가 totalPages에 가까우면 startPage를 조정
                        if (endPage - startPage + 1 < maxVisiblePages) {
                            startPage = Math.max(1, endPage - maxVisiblePages + 1);
                        }

                        const pages = [];

                        // 첫 페이지 표시
                        if (startPage > 1) {
                            pages.push(
                                <button
                                    key={1}
                                    className="sds-pagination__btn"
                                    onClick={() => setPage(1)}
                                >
                                    1
                                </button>
                            );
                            if (startPage > 2) {
                                pages.push(<span key="ellipsis1" className="sds-pagination__ellipsis">…</span>);
                            }
                        }

                        // 중간 페이지들 표시
                        for (let i = startPage; i <= endPage; i++) {
                            pages.push(
                                <button
                                    key={i}
                                    className={`sds-pagination__btn${currentPage === i ? ' is-active' : ''}`}
                                    aria-current={currentPage === i ? 'page' : undefined}
                                    onClick={() => setPage(i)}
                                >
                                    {i}
                                </button>
                            );
                        }

                        // 마지막 페이지 표시
                        if (endPage < totalPages) {
                            if (endPage < totalPages - 1) {
                                pages.push(<span key="ellipsis2" className="sds-pagination__ellipsis">…</span>);
                            }
                            pages.push(
                                <button
                                    key={totalPages}
                                    className="sds-pagination__btn"
                                    onClick={() => setPage(totalPages)}
                                >
                                    {totalPages}
                                </button>
                            );
                        }

                        return pages;
                    })()}
                    <button
                        className="sds-pagination__btn"
                        onClick={() => setPage(Math.min(page + 1, data.totalPages))}
                        disabled={page >= data.totalPages}
                        aria-label="다음 페이지"
                    >
                        <ChevronRight size={16} aria-hidden="true" />
                    </button>
                </div>
            </>
        </div>
    );
};

export default function Dashboard() {
    return (
        <div className="dashboard-container dashboard-page">
            <NavigationBar />
            <div className="dashboard-main">
                <StatsAndTable />
            </div>
        </div>
    );
}
